import { ChallengeTableChallenge } from '../../../../api/types/dtos/challenge/ChallengeDto';
import { AnswerDto } from '../../../../api/services/answer.service';
import {
  OrganizationField,
  CellMapping,
  FilterCondition,
  AggregationType,
  PositionSelector,
  ProcessedCellValue,
  ChallengeFilter,
} from '../types/reportBuilder.types';
import {

  getQuestionAnswers,

  } from './reportBuilder.parser';

/**
 * Get field value from challenge
 */
export function getFieldValue(
  challenge: ChallengeTableChallenge,
  field: OrganizationField
): any {
  switch (field) {
    case 'inspectedOrgId':
      return challenge.inspectedOrgId;
    case 'inspectedOrgName':
      return challenge.inspectedOrgName;
    case 'inspectedOrgLogo':
      return challenge.inspectedOrgLogo;
    case 'inspectedUserId':
      return challenge.inspectedUserId;
    case 'inspectedUserIdNumber':
      return challenge.inspectedUserIdNumber;
    case 'inspectedUserName':
      return challenge.inspectedUserName;
    case 'inspectedUserPhones':
      return challenge.inspectedUserPhones || [];
    case 'inspectedOrgGroupsName':
      return challenge.inspectedOrgGroupsName || [];
    case 'inspectedOrgRegionName':
      return challenge.inspectedOrgRegionName;
    case 'inspectedOrgCityName':
      return challenge.inspectedOrgCityName;
    case 'inspectedOrgVeredaName':
      return challenge.inspectedOrgVeredaName;
    default:
      return null;
  }
}

/**
 * Format a Date object as DD-MM-YYYY
 */
export function formatDateDDMMYYYY(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

/**
 * Apply position selector to array values
 */
export function applyPositionSelector(
  values: any[],
  selector: PositionSelector
): any {
  if (!Array.isArray(values) || values.length === 0) {
    return null;
  }

  switch (selector.type) {
    case 'first':
      return values[0];
    case 'last':
      return values[values.length - 1];
    case 'nth':
      if (selector.index !== undefined && selector.index >= 0 && selector.index < values.length) {
        return values[selector.index];
      }
      return null;
    case 'all':
      return values;
    default:
      return values[0];
  }
}

/**
 * Convert value to string for proper ordering
 */
export function convertToStringForOrdering(value: any): string {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'number') {
    // Pad numbers to ensure proper string ordering (e.g., "001", "010", "100")
    return String(value).padStart(10, '0');
  }

  return String(value);
}

/**
 * Flatten nested arrays recursively
 */
function flattenArray(arr: any[]): any[] {
  const result: any[] = [];
  for (const item of arr) {
    if (Array.isArray(item)) {
      result.push(...flattenArray(item));
    } else {
      result.push(item);
    }
  }
  return result;
}

/**
 * Apply aggregation to values
 */
export function applyAggregation(
  values: any[],
  aggregation: AggregationType,
  separator: string = ', ',
  questionType?: string
): any {
  if (!Array.isArray(values) || values.length === 0) {
    return null;
  }

  // Flatten nested arrays first
  const flattened = flattenArray(values);

  // Filter out null/undefined values
  const validValues = flattened.filter(v => v !== null && v !== undefined);
  if (validValues.length === 0) {
    return null;
  }

  switch (aggregation) {
    case 'concat':
      return validValues.map(v => String(v)).join(separator);

    case 'max': {
      // For date/datetime questions, treat as dates
      if (questionType === 'date' || questionType === 'datetime') {
        const dateValues = validValues.map(v => {
          const date = new Date(v);
          return isNaN(date.getTime()) ? null : date;
        }).filter(d => d !== null) as Date[];

        if (dateValues.length > 0) {
          // All values are valid dates, return the latest date
          const maxDate = new Date(Math.max(...dateValues.map(d => d.getTime())));
          return maxDate.toISOString();
        }
      }

      // For all other types, use numeric comparison
      const numbers = validValues.map(v => {
        const num = typeof v === 'string' ? parseFloat(v) : Number(v);
        return isNaN(num) ? null : num;
      }).filter(n => n !== null) as number[];
      return numbers.length > 0 ? Math.max(...numbers) : null;
    }

    case 'min': {
      // For date/datetime questions, treat as dates
      if (questionType === 'date' || questionType === 'datetime') {
        const minDateValues = validValues.map(v => {
          const date = new Date(v);
          return isNaN(date.getTime()) ? null : date;
        }).filter(d => d !== null) as Date[];

        if (minDateValues.length > 0) {
          // All values are valid dates, return the earliest date
          const minDate = new Date(Math.min(...minDateValues.map(d => d.getTime())));
          return minDate.toISOString();
        }
      }

      // For all other types, use numeric comparison
      const minNumbers = validValues.map(v => {
        const num = typeof v === 'string' ? parseFloat(v) : Number(v);
        return isNaN(num) ? null : num;
      }).filter(n => n !== null) as number[];
      return minNumbers.length > 0 ? Math.min(...minNumbers) : null;
    }

    case 'avg': {
      const avgNumbers = validValues.map(v => {
        const num = typeof v === 'string' ? parseFloat(v) : Number(v);
        return isNaN(num) ? null : num;
      }).filter(n => n !== null) as number[];
      if (avgNumbers.length === 0) return null;
      const sum = avgNumbers.reduce((a, b) => a + b, 0);
      return sum / avgNumbers.length;
    }

    case 'count':
      // Count the number of elements in the array
      return validValues.length;

    case 'first':
      return validValues[0];

    case 'last':
      return validValues[validValues.length - 1];

    case 'all':
      return validValues;

    default:
      return validValues[0];
  }
}

/**
 * Evaluate filter condition
 */
export function evaluateFilter(
  value: any,
  condition: FilterCondition
): boolean {
  if (value === null || value === undefined) {
    return false;
  }

  const { operator, value: conditionValue, valueType } = condition;

  switch (operator) {
    case 'is':
      return String(value) === String(conditionValue);

    case 'isNot':
      return String(value) !== String(conditionValue);

    case 'contains':
      return String(value).toLowerCase().includes(String(conditionValue).toLowerCase());

    case 'startsWith':
      return String(value).toLowerCase().startsWith(String(conditionValue).toLowerCase());

    case 'endsWith':
      return String(value).toLowerCase().endsWith(String(conditionValue).toLowerCase());

    case 'between':
      if (Array.isArray(conditionValue) && conditionValue.length === 2) {
        const numValue = typeof value === 'string' ? parseFloat(value) : Number(value);
        const min = typeof conditionValue[0] === 'string' ? parseFloat(conditionValue[0]) : Number(conditionValue[0]);
        const max = typeof conditionValue[1] === 'string' ? parseFloat(conditionValue[1]) : Number(conditionValue[1]);
        if (!isNaN(numValue) && !isNaN(min) && !isNaN(max)) {
          return numValue >= min && numValue <= max;
        }
        // For dates
        if (valueType === 'date') {
          const dateValue = new Date(value);
          const dateMin = new Date(conditionValue[0]);
          const dateMax = new Date(conditionValue[1]);
          return dateValue >= dateMin && dateValue <= dateMax;
        }
      }
      return false;

    case 'gt': {
      const gtNumValue = typeof value === 'string' ? parseFloat(value) : Number(value);
      const gtCondition = typeof conditionValue === 'string' ? parseFloat(conditionValue) : Number(conditionValue);
      if (!isNaN(gtNumValue) && !isNaN(gtCondition)) {
        return gtNumValue > gtCondition;
      }
      if (valueType === 'date') {
        return new Date(value) > new Date(conditionValue);
      }
      return false;
    }

    case 'less': {
      const lessNumValue = typeof value === 'string' ? parseFloat(value) : Number(value);
      const lessCondition = typeof conditionValue === 'string' ? parseFloat(conditionValue) : Number(conditionValue);
      if (!isNaN(lessNumValue) && !isNaN(lessCondition)) {
        return lessNumValue < lessCondition;
      }
      if (valueType === 'date') {
        return new Date(value) < new Date(conditionValue);
      }
      return false;
    }

    default:
      return false;
  }
}

/**
 * Apply filters to values
 */
export function applyFilters(
  values: any[],
  filters: FilterCondition[]
): any[] {
  if (!filters || filters.length === 0) {
    return values;
  }

  return values.filter(value => {
    return filters.every(filter => evaluateFilter(value, filter));
  });
}

/**
 * Get question answer values from challenge
 */
export function getQuestionAnswerValues(
  challenge: ChallengeTableChallenge,
  questionId: string,
  programVersionItemId: string
): any[] {
  const item = challenge.inspectionsProgramsVersionsItems.find(
    i => i.programVersionItemId === programVersionItemId
  );

  if (!item) {
    return [];
  }

  const answers = getQuestionAnswers(item, questionId);
  // Extract values and flatten if they're arrays (for multiselect/checkbox)
  const values: any[] = [];
  answers.forEach(a => {
    if (Array.isArray(a.value)) {
      // If the value is already an array, spread it into the result
      values.push(...a.value);
    } else {
      values.push(a.value);
    }
  });
  return values;
}

/**
 * Process cell value for a challenge
 */
export function processCellValue(
  challenge: ChallengeTableChallenge,
  mapping: CellMapping,
  answersMap?: Record<string, AnswerDto[]>
): ProcessedCellValue {
  try {
    let value: any = null;

    switch (mapping.sourceType) {
      case 'field':
        if (mapping.sourceId) {
          value = getFieldValue(challenge, mapping.sourceId as OrganizationField);

          // Handle array fields
          if (Array.isArray(value) && mapping.positionSelector) {
            value = applyPositionSelector(value, mapping.positionSelector);
          } else if (Array.isArray(value) && !mapping.positionSelector) {
            value = value.join(', ');
          }
        }
        break;

      case 'question':
        if (mapping.sourceId && mapping.sourceId.includes(':')) {
          // Handle question with programVersionItemId:questionUuid format
          // qId is the question UUID (matches AnswerDto.questionId)
          const [pviId, questionUuid] = mapping.sourceId.split(':');
          
          let questionValues: any[] = [];
          
          // Try to use answersMap first (new system)
          if (answersMap) {
            // Find the inspection program item for this challenge
            const inspectionItem = challenge.inspectionsProgramsVersionsItems.find(
              i => i.programVersionItemId === pviId
            );
            
            if (inspectionItem && inspectionItem.id) {
              // Get answers for this inspection item from the batch-loaded map
              // inspectionItem.id is the InspectionsProgramsItem.id which matches AnswerDto.inspectionProgramItemId
              const itemAnswers = answersMap[inspectionItem.id] || [];
              
              // Find answers for this specific question (by UUID)
              const questionAnswers = itemAnswers.filter(
                a => a.questionId === questionUuid
              );
              
              // Extract values (handle arrays for multiselect)
              questionAnswers.forEach(a => {
                let extractedValue = a.value;
                
                // If value is a string that looks like JSON, parse it
                if (typeof a.value === 'string' && 
                    (a.value.startsWith('[') || a.value.startsWith('{"'))) {
                  try {
                    extractedValue = JSON.parse(a.value);
                  } catch (e) {
                    // If parsing fails, keep original value
                    console.warn('Failed to parse JSON value:', a.value);
                  }
                }
                
                if (Array.isArray(extractedValue)) {
                  questionValues.push(...extractedValue);
                } else {
                  questionValues.push(extractedValue);
                }
              });
              
              if (questionAnswers.length > 1) {
                console.log(`📊 Multiple answers for question ${questionUuid}:`, {
                  count: questionAnswers.length,
                  values: questionAnswers.map(a => ({ 
                    value: a.value, 
                    forEachContext: a.forEachContextValue 
                  }))
                });
              }
            } else {
              console.warn('✗ Inspection item not found:', {
                pviId,
                availableItems: challenge.inspectionsProgramsVersionsItems.map(i => ({
                  id: i.id,
                  programVersionItemId: i.programVersionItemId
                }))
              });
            }
          } else {
            // Fallback to old method (parsing from item.value)
            questionValues = getQuestionAnswerValues(challenge, questionUuid, pviId);
          }
          
          // Apply filters if any
          if (mapping.filters && mapping.filters.length > 0) {
            questionValues = applyFilters(questionValues, mapping.filters);
          }

          // Apply position selector
          if (mapping.positionSelector && questionValues.length > 0) {
            questionValues = [applyPositionSelector(questionValues, mapping.positionSelector)];
          }

          // Apply aggregation
          if (mapping.aggregation && questionValues.length > 0) {
            value = applyAggregation(questionValues, mapping.aggregation, mapping.separator, mapping.questionType);
            console.log('📊 Aggregation result:', {
              aggregation: mapping.aggregation,
              inputCount: questionValues.length,
              result: value,
              resultType: typeof value,
              questionType: mapping.questionType
            });
          } else if (questionValues.length > 0) {
            value = questionValues.length === 1 ? questionValues[0] : questionValues;
          }
        }
        break;

      case 'itemComment':
        if (mapping.sourceId) {
          // sourceId is programVersionItemId
          const item = challenge.inspectionsProgramsVersionsItems.find(
            i => i.programVersionItemId === mapping.sourceId
          );
          if (item && item.comments) {
            value = item.comments;
          }
        }
        break;

      case 'constant':
        value = mapping.constantValue;
        break;

      case 'default':
        value = mapping.defaultValue || '';
        break;
    }

    // Convert to string for CSV if needed
    if (value !== null && value !== undefined) {
      if (Array.isArray(value)) {
        value = value.join(', ');
      } else if (value instanceof Date) {
        // Format based on question type
        if (mapping.questionType === 'date') {
          value = formatDateDDMMYYYY(value);
        } else {
          value = value.toISOString();
        }
      } else if (typeof value === 'string' && mapping.questionType === 'date') {
        // Try to parse and format date strings for 'date' type questions
        const parsedDate = new Date(value);
        if (!isNaN(parsedDate.getTime())) {
          value = formatDateDDMMYYYY(parsedDate);
        }
      } else if (typeof value === 'number') {
        // Only format as date if the question type is actually 'date' or 'datetime'
        if (mapping.questionType === 'date' || mapping.questionType === 'datetime') {
          console.log('🔍 About to format as date:', { value, questionType: mapping.questionType, sourceId: mapping.sourceId });
          const dateValue = new Date(value);
          if (!isNaN(dateValue.getTime())) {
            value = formatDateDDMMYYYY(dateValue);
          }
        }
        // For all other question types (number, text, multiselect, etc), keep the number as-is
        else {
          value = String(value);
        }
      } else {
        value = String(value);
      }
    } else {
      value = mapping.defaultValue || '';
    }

    return {
      cellId: mapping.cellId,
      value: value || '',
    };
  } catch (error: any) {
    return {
      cellId: mapping.cellId,
      value: '',
      error: error.message || 'Error processing cell value',
    };
  }
}

/**
 * Apply initial filters to challenges
 */
export function applyInitialFilters(
  challenges: ChallengeTableChallenge[],
  filters: ChallengeFilter[]
): ChallengeTableChallenge[] {
  if (!filters || filters.length === 0) {
    return challenges;
  }

  return challenges.filter(challenge => {
    return filters.every(filter => {
      const fieldValue = getFieldValue(challenge, filter.field as OrganizationField);
      // Simple evaluation - can be enhanced
      return evaluateFilter(fieldValue, {
        sourceType: 'field',
        sourceId: filter.field,
        operator: filter.operator,
        value: filter.value,
      });
    });
  });
}

/**
 * Generate CSV content
 */
export function generateCSV(
  headers: string[],
  rows: ProcessedCellValue[][]
): string {
  const escapeCSV = (value: string) => {
    if (value === null || value === undefined) {
      return '""';
    }
    const str = String(value);
    // Escape quotes and wrap in quotes
    return `"${str.replace(/"/g, '""')}"`;
  };

  const csvRows = [
    headers.map(escapeCSV).join(','),
    ...rows.map(row => row.map(cell => escapeCSV(cell.value)).join(',')),
  ];

  return csvRows.join('\n');
}

/**
 * Download CSV file
 */
export function downloadCSV(content: string, filename: string = 'report.csv'): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
